import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Brand";
import { PLANS, TRIAL_DAYS, findCity } from "@/config/site";
import { getMember, saveMember } from "@/services/demoMember";
import { startStripeSignup } from "@/services/signup";

const PLAN = PLANS.find((p) => p.id === "annuelle");
const yearly = Number(PLAN.price.replace(/[^\d,]/g, "").replace(",", "."));
const [euros, cents] = (yearly / 12).toFixed(2).split(".");

const PriceCard = ({ onStart, busy, member }) => (
  <div data-testid="plan-price-card" className="mt-8 rounded-[24px] bg-ivory px-6 py-8 text-center shadow-[0_20px_40px_-24px_rgba(0,0,0,0.6)] sm:px-10">
    <p className="text-sm font-semibold text-navy/60">Après votre essai, continuez pour</p>
    <p data-testid="plan-monthly-price" className="mt-4 flex items-baseline justify-center font-display text-navy">
      <span className="text-7xl leading-none">{euros}</span>
      <span className="text-3xl">,{cents} €</span>
      <span className="ml-2 font-sans text-lg text-navy/60">/mois</span>
    </p>
    <button type="button" onClick={onStart} disabled={busy} data-testid="plan-continue-button" className="btn-gold group mt-7 w-full justify-center py-4 text-base disabled:opacity-60">
      {busy ? "Redirection vers le paiement sécurisé…" : `Commencer mes ${TRIAL_DAYS} jours gratuits`}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
    <p data-testid="plan-billing-note" className="mt-5 text-sm leading-relaxed text-navy/70">
      <strong className="font-semibold text-navy">{PLAN.price} prélevés en une fois pour un an</strong>.
    </p>
    <Recap member={member} />
    <p data-testid="plan-refund-guarantee" className="mt-1.5 text-sm text-navy/60">
      Remboursé si vous n'économisez pas 500&nbsp;€ ·{" "}
      <Link to="/garantie" data-testid="plan-guarantee-link" className="underline underline-offset-2 hover:text-navy">Conditions</Link>
    </p>
  </div>
);

const Recap = ({ member }) => {
  const city = findCity(member.city);
  return (
    <p data-testid="plan-signup-recap" className="mt-3 text-xs text-navy/55">
      Inscription pour <strong className="font-semibold text-navy/80">{[member.civility, member.firstName, member.lastName].filter(Boolean).join(" ")}</strong> ({member.email})
      {" · "}départ de {city ? city.name : member.city} ·{" "}
      <Link to="/inscription" data-testid="plan-edit-signup-link" className="underline underline-offset-2 hover:text-navy">Modifier</Link>
    </p>
  );
};

export default function Subscribe() {
  const [busy, setBusy] = useState(false);
  const member = getMember();
  if (!member.city || !member.email || !member.lastName) return <Navigate to="/inscription" replace />;

  const go = async () => {
    setBusy(true);
    saveMember({ plan: PLAN.id });
    try {
      await startStripeSignup(member);
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.status === 400 ? e.response.data.detail : "Impossible d'ouvrir la page de paiement. Réessayez dans un instant.");
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="flex justify-center px-5 pb-4 pt-8 sm:pt-12"><Logo tone="dark" /></header>
      <main className="mx-auto max-w-xl px-4 pb-16 sm:px-6">
        <motion.section
          data-testid="plan-offer-card"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="rounded-[32px] bg-navy px-5 py-10 text-center shadow-[0_30px_60px_-30px_rgba(11,21,48,0.7)] sm:px-10 sm:py-12"
        >
          <h1 className="font-display text-4xl text-ivory sm:text-5xl">
            Essayez le club <span className="text-gold-gradient">gratuitement</span>
          </h1>
          <p className="mt-5 text-base font-semibold text-ivory">Accédez dès aujourd'hui aux offres Business au départ de Paris-CDG.</p>
          <p className="mt-2 text-sm leading-relaxed text-ivory/70 sm:text-base">
            Aucun prélèvement avant la fin de vos {TRIAL_DAYS} jours d'essai.<br />Résiliable à tout moment.
          </p>
          <PriceCard onStart={go} busy={busy} member={member} />
        </motion.section>
      </main>
    </div>
  );
}
